import { useCallback, useMemo } from 'react';
import useQuery from '../../../helpers/JIFI/useQuery';
import {
    REGION_COLUMNS,
    cellAvailable,
    regionTotals,
    unallocatedFor,
} from './stockAvailability';

const LOCATION_ALIASES = {
    'HQ': 'HQ2',
    'EWE MEL': 'EWE',
    'EWE SYD': 'EWE - Sydney',
    'EWE BRI': 'EWE - Brisbane',
    'EWE PER': 'EWE - Perth',
    'EWE ADL': 'EWE - Adelaide',
    'GREEN ST': 'NYC Greene St',
    'UNIS': 'UNIS',
    'UTAH': 'LCHSLC',
    'KENTUCKT': 'LCHKTC',
    'AMAZON': 'Amazon',
    'AIRBOX': 'Airbox UK',
    'AMAZON UK': 'Amazon UK',
};

const AU_RETAIL_LOCATIONS = REGION_COLUMNS.AU.slice(6).map(([, locationName]) => locationName);

const useSKULevels = (refresh) => {
    const [availabilityData] = useQuery({
        url: `/product/total-stock-availability`,
        queryData: { cacheBust: refresh },
        refresh,
    });

    const rowsBySku = useMemo(() => new Map(
        (availabilityData?.rows || []).map(row => [row.sku, row])
    ), [availabilityData]);

    return useCallback((sku, metric) => {
        if (!availabilityData) return undefined;

        const row = rowsBySku.get(sku);
        if (!row) return null;

        if (['AU', 'US', 'UK', 'NZ'].includes(metric)) {
            return regionTotals(row, metric).available;
        }

        const reservedRegion = metric.replace(/^RESERVED /, '');
        if (reservedRegion !== metric) {
            return regionTotals(row, reservedRegion).reserved;
        }

        const unallocatedRegion = metric.replace(/^UNALLOCATED /, '');
        if (unallocatedRegion !== metric) {
            return unallocatedFor(row, unallocatedRegion);
        }

        if (metric === 'AU RETAIL') {
            return AU_RETAIL_LOCATIONS.reduce(
                (total, locationName) => total + cellAvailable(row.cells?.[locationName]),
                0
            );
        }

        const locationName = LOCATION_ALIASES[metric];
        return locationName ? cellAvailable(row.cells?.[locationName]) : undefined;
    }, [availabilityData, rowsBySku]);
};

export default useSKULevels;
